import NewForm from "components/NewForm";
import { handleScrollToErrors } from "components/NewForm/utils";
import DeleteModal from "components/shared/DeleteModal";
import { FilterOption } from "components/shared/FiltersAutocomplete";
import type { IFormStep } from "containers/TermFormContainer";
import type { IWork } from "containers/Works/types";
import TransfereesFormContainer from "containers/TransfereesFormContainer";
import { getCountries } from "countries";
import { formatLocaleDateString, replaceComma } from "helpers/utils";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import React from "react";
import type {
    Control,
    FieldErrors,
    UseFormClearErrors,
    UseFormSetError,
    UseFormSetValue,
    UseFormWatch,
} from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchWork } from "store/workStore";
import type { ITransferee } from "store/transfereesStore";
import { fetchTransferee, fetchTransfereesAutocomplete } from "store/transfereesStore";
import type { ITransferTermSale } from "store/transferTermSalesStore";
import { fetchTransferTermSales } from "store/transferTermSalesStore";
import type { ITransferTerm, ITransferTermAdvance } from "store/transferTermsStore";
import {
    createTransferTerm,
    deleteTransferTerm,
    ETransferTermStatus,
    updateTransferTerm,
} from "store/transferTermsStore";
import type { TFetchStatus } from "store/types";
import { v4 as uuidv4 } from "uuid";

import DateStep from "./DatesStep";
import NewTermStep from "./NewTermStep";
import SettingsStep from "./SettingStep";
import { checkHaveCountriesChanged, formatCountriesForFilter, formatTimestampDates, transferWorkLabel } from "./utils";

interface ITransferTermFormContainer {
    /**
     * Is form being accesses on creation or update.
     */
    mode: "new" | "edit";
    /**
     * Triggers closing edit mode.
     */
    closeEdit?: () => void;
    /**
     * Data of term for update.
     */
    term?: ITransferTerm | undefined;
    /**
     * Needed to display block modal on update.
     */
    setIsFormDirty?: Dispatch<SetStateAction<boolean>>;
    /**
     * Allows us to update term data on drawer once update made (as data there is fetched from getAll).
     */
    onEdit?: (term: ITransferTerm) => void;
    /**
     * Triggers closing drawer when accessing form from TransferTermDrawer.
     */
    onDelete?: () => void;
    /**
     * Array with advance sales > needed on edit.
     */
    termAdvances?: ITransferTermSale[];
    /**
     * Array with term sales.
     */
    termSales?: ITransferTermSale[];
}

export interface ITransferTermData {
    work: IWork;
    transferee: ITransferee;
    upcomingPaymentDate: Date;
    startDate: Date;
    expirationDate: Date;
    advancePayment: ITransferTermAdvance[];
    countryCodes: string[];
    status: ETransferTermStatus;
    periodDuration: number;
    paymentDelay: number;
    initialGrossAmount: number;
    reference: string | null;
}

export interface ITransferTermFormValues {
    workId: number | null;
    work: IWork | null;
    transfereeId: number | null;
    transferee: ITransferee | null;
    upcomingPaymentDate: number | Date | undefined | null;
    startDate: number | Date | undefined | null;
    expirationDate: number | Date | undefined | null;
    advancePayment: ITransferTermAdvance[];
    periodDuration: number | null;
    paymentDelay: number | null;
    countryCodes: string[] | undefined;
    status: ETransferTermStatus | undefined;
    initialGrossAmount: number;
    reference: string;
}

export interface ITransferTermFormData {
    control: Control<ITransferTermFormValues>;
    errors: FieldErrors<ITransferTermFormValues>;
    watch: UseFormWatch<ITransferTermFormValues>;
    setValue: UseFormSetValue<ITransferTermFormValues>;
    isDirty?: boolean;
    setError?: UseFormSetError<ITransferTermFormValues>;
    clearErrors?: UseFormClearErrors<ITransferTermFormValues>;
}

/**
 * Form to create a transferTerm. Accessed either from TransferTerms page or a Transferee page.
 */
const TransferTermFormContainer = ({
    mode,
    closeEdit,
    term,
    setIsFormDirty,
    onEdit,
    onDelete,
    termAdvances,
    termSales,
}: ITransferTermFormContainer) => {
    const dispatch = useAppDispatch();
    const nav = useNavigate();
    const [t] = useTranslation();
    const { state } = useLocation();
    const termDuplicate = state?.term;
    /**
     * Passed when creating new term form transferee page.
     */
    const transfereeId = state?.transfereeId;
    const from = state?.from;

    const [termStatus, setTermStatus] = useState<ETransferTermStatus>(
        termDuplicate ? ETransferTermStatus.UNDER_NEGOTIATION : ETransferTermStatus.UP_TO_DATE,
    );
    const [chosenCountries, setChosenCountries] = useState([] as { id: string; name: string }[]);
    const [addNewTransferee, setAddNewTransferee] = useState<boolean>();
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [submitFormStatus, setSubmitFormStatus] = useState<TFetchStatus>("idle");

    const doesGrossNotEqualNet = useMemo(() => {
        return termAdvances?.some((item) => item.grossAmount !== item.netAmount);
    }, [termAdvances]);
    const [grossNotEqualNet, setGrossNotEqualNet] = useState<boolean>(doesGrossNotEqualNet ?? false); // Used for advancePayment

    const { selectedWork } = useAppSelector((state) => state.works);
    const { transferee, transferees } = useAppSelector((state) => state.transferees);
    const { transferTerms } = useAppSelector((state) => state.transferTerms);

    const hasTermStatusChanged = termStatus !== term?.status;
    const haveCountriesChanged = term ? checkHaveCountriesChanged(chosenCountries, term?.countryCodes) : false;

    const defaultValues: ITransferTermFormValues = useMemo(() => {
        const formattedAdvancePayments = termAdvances?.reverse().map((advance, index) => ({
            id: advance.id,
            grossAmount: advance.grossAmount / 100,
            netAmount: advance.netAmount / 100,
            isPaid: advance.status === 6 ? 1 : 0,
            order: index,
            paymentDate: advance.paymentDate,
            status: advance.status,
        }));

        const pageTransferee = transferees.find((item) => item.id === transfereeId);

        return {
            workId: mode === "edit" ? term?.workId : termDuplicate ? termDuplicate.workId : null,
            work: mode === "edit" ? selectedWork : null,
            // On edit we use data from term. When creating duplicate we use data from term duplicate. When creating new term from transferee page we use that transfereeId.
            transfereeId:
                mode === "edit"
                    ? term?.transfereeId
                    : termDuplicate
                      ? termDuplicate.transfereId
                      : transfereeId
                        ? transfereeId
                        : null,
            transferee: mode === "edit" && transferee ? transferee : pageTransferee ? pageTransferee : null,
            upcomingPaymentDate: null, // This value is not saved. So they can change it but we don't know which date they'd originally set on edit.
            startDate: mode === "edit" ? term?.startDate : null,
            expirationDate: mode === "edit" ? term?.expirationDate : null,
            advancePayment:
                mode === "edit" && formattedAdvancePayments?.length
                    ? formattedAdvancePayments
                    : [
                          {
                              grossAmount: 0,
                              netAmount: 0,
                              isPaid: 0,
                              order: 0,
                              paymentDate: null,
                              status: 5,
                              id: uuidv4(),
                          },
                      ],
            periodDuration: mode === "edit" ? term?.periodDuration : termDuplicate ? termDuplicate?.periodDuration : 0,
            paymentDelay: mode === "edit" ? term?.paymentDelay : termDuplicate ? termDuplicate?.paymentDelay : 0,
            countryCodes: mode === "edit" ? term?.countryCodes : [],
            status:
                mode === "edit"
                    ? term?.status
                    : termDuplicate
                      ? ETransferTermStatus.UNDER_NEGOTIATION
                      : ETransferTermStatus.UP_TO_DATE,
            initialGrossAmount: mode === "edit" ? (term?.initialGrossAmount ?? 0) / 100 : 0,
            reference: mode === "edit" ? term?.reference ?? "" : "",
        };
    }, [mode, term, transferee, selectedWork, termAdvances]);

    const {
        control,
        handleSubmit,
        trigger,
        formState: { isDirty, dirtyFields, errors },
        watch,
        setValue,
        clearErrors,
        setError,
    } = useForm<ITransferTermFormValues>({ defaultValues, mode: "all" });

    const chosenWork = watch("work");
    const chosenTransferee = watch("transferee");

    const onSubmit = async (data: ITransferTermData) => {
        setSubmitFormStatus("pending");
        const countriesCode: string[] = chosenCountries.map((country) => country.id as string);

        const advancePayment = data.advancePayment.map((payment) => {
            return {
                ...payment,
                id: mode === "new" ? null : typeof payment.id === "number" ? payment.id : null,
                isPaid: !!payment.isPaid,
                paymentDate: payment.paymentDate ? formatTimestampDates(payment.paymentDate) : null,
                grossAmount: +replaceComma(payment.grossAmount.toString()) * 100,
                netAmount:
                    +replaceComma(grossNotEqualNet ? payment.netAmount.toString() : payment.grossAmount.toString()) *
                    100,
                status: payment.isPaid === 1 ? 6 : 5,
            };
        });

        const noAdvance = advancePayment.length === 1 && advancePayment[0].grossAmount === 0;

        const req = {
            workId: data.work.id,
            titleId: null,
            transfereeId: data.transferee.id,
            upcomingPaymentDate: formatTimestampDates(data.upcomingPaymentDate ?? new Date()) ?? null,
            startDate: formatTimestampDates(data.startDate ?? new Date()) ?? null,
            expirationDate: formatTimestampDates(data.expirationDate) ?? null,
            advancePayment: noAdvance ? [] : advancePayment ?? [],
            countryCodes: countriesCode ?? [],
            status: termStatus ?? ETransferTermStatus.UP_TO_DATE,
            periodDuration: +data.periodDuration || 0,
            paymentDelay: +data.paymentDelay || 0,
            initialGrossAmount: Math.round(+data.initialGrossAmount * 100) ?? 0,
            reference: data?.reference ?? null,
            percentages: [
                {
                    percentage: 0,
                    quantity: 1,
                },
            ], // randomly setting this as we're not using it
        };

        if (mode === "new") {
            const response = await dispatch(createTransferTerm({ data: req }));
            if (response.meta.requestStatus === "fulfilled") {
                if (from === "transfereePage") {
                    nav(-1);
                } else {
                    dispatch(fetchTransferTermSales({ options: { limit: 100, sortBy: "status", sortOrder: "asc" } }));
                    nav(`/transfer-terms`, { state: { tabValue: "1" } });
                }
                setSubmitFormStatus("succeeded");
            } else {
                setSubmitFormStatus("failed");
            }
        } else if (mode === "edit" && term) {
            const response = await dispatch(
                updateTransferTerm({ data: req, transferTermId: term?.id, sales: termSales ?? [] }),
            );
            if (response.meta.requestStatus === "fulfilled") {
                onEdit && onEdit(response.payload);
                closeEdit && closeEdit();
                setSubmitFormStatus("succeeded");
            } else {
                setSubmitFormStatus("failed");
            }
        }
    };

    const handleClosingTransfereeForm = async (transferee: ITransferee) => {
        const options = { limit: 10, sortOrder: "desc" };
        const response = await dispatch(fetchTransfereesAutocomplete({ options }));

        if (transferee && response.meta.requestStatus === "fulfilled") {
            await setValue("transferee", transferee);
            await clearErrors("transferee");
            setAddNewTransferee(false);
        }
    };

    const handleDeleteTerm = async () => {
        if (term) {
            const response = await dispatch(deleteTransferTerm({ transferTermId: term.id }));
            if (response.meta.requestStatus === "fulfilled") {
                closeEdit && closeEdit();
                if (from === "termDrawer") {
                    dispatch(fetchTransferTermSales({ options: { limit: 100, sortBy: "status", sortOrder: "asc" } }));
                    onDelete && onDelete();
                }
            }
        }
    };

    const steps: IFormStep[] = [
        {
            stepTitle: mode === "new" ? t("TransferTerms.TermForm.NewTerm") : t("TransferTerms.TermForm.UpdateTerm"),
            stepSubtitle: t("TransferTerms.TermForm.NewTermSubtitle"),
            conditionToDisable: !chosenCountries.length || !chosenWork,
            format: (stepTitle, stepSubtitle) => (
                <NewTermStep
                    indexStep={0}
                    stepSubtitle={stepSubtitle}
                    stepTitle={stepTitle}
                    useFormData={{ control, errors, watch, setValue, setError, clearErrors }}
                    chosenCountries={chosenCountries}
                    setChosenCountries={setChosenCountries}
                    mode={mode}
                    setAddNewTransferee={setAddNewTransferee}
                    from={from}
                    allTransferTerms={transferTerms}
                    term={term}
                    haveCountriesChanged={haveCountriesChanged}
                />
            ),
        },
        {
            stepTitle: t("TransferTerms.TermForm.StatusStepTitle"),
            stepSubtitle: t("TransferTerms.TermForm.StatusStepSubtitle"),
            conditionToDisable: !!errors.expirationDate,
            format: (stepTitle, stepSubtitle) => (
                <SettingsStep
                    indexStep={1}
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    useFormData={{ control, errors, watch, setError, clearErrors, setValue }}
                    termStatus={termStatus}
                    setTermStatus={setTermStatus}
                    grossNotEqualNet={grossNotEqualNet}
                    setGrossNotEqualNet={setGrossNotEqualNet}
                />
            ),
        },
        {
            stepTitle: t("TransferTerms.TermForm.DatesStepTitle"),
            stepSubtitle: t("TransferTerms.TermForm.DatesStepSubtitle"),
            conditionToDisable: +(watch("periodDuration") ?? "0") === 0,
            format: (stepTitle, stepSubtitle) => (
                <DateStep
                    indexStep={2}
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    useFormData={{ control, errors, watch, setValue }}
                    mode={mode}
                />
            ),
        },
    ];

    const dataForSteps = [`${chosenWork?.name ?? ""} <> ${chosenTransferee?.name ?? ""}`];

    useEffect(() => {
        if ((mode === "edit" && term) || termDuplicate) {
            const workId = term?.workId ?? termDuplicate?.workId;
            if (workId) {
                dispatch(fetchWork(workId)).then((resp) => {
                    if (fetchWork.fulfilled.match(resp)) {
                        setValue("work", resp.payload);
                    }
                });
            }
            dispatch(fetchTransferee({ transfereeId: term?.transfereeId ?? termDuplicate?.transfereeId })).then(
                (resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        setValue("transferee", resp.payload);
                    }
                },
            );

            const termCountries = getCountries().filter((item) =>
                (term?.countryCodes ?? termDuplicate.countryCodes).includes(item.code),
            );
            const formattedCountries = formatCountriesForFilter(termCountries);
            setChosenCountries(formattedCountries);
        }
    }, [term, mode]);

    useEffect(() => {
        if (mode === "edit") {
            if (isDirty) {
                setIsFormDirty && setIsFormDirty(isDirty || haveCountriesChanged || hasTermStatusChanged);
            }
        }
    }, [isDirty]);

    /**
     * Text specific to the Archiving step.
     **/
    const deleteStep = {
        title: t("TransferTerms.TermForm.DeleteTermTitle"),
        subtitle: t("TransferTerms.TermForm.DeleteTermSubtitle"),
    };

    const deleteTermModalDescription = (
        <>
            <span>{t("DeleteModal.TermInfo")}</span>
            <p>{t("DeleteModal.Title", { titleName: transferWorkLabel(term ?? {}) })}</p>
            <p>{t("TransferTerms.DeleteModal.Cessionnaire", { name: term?.transfereeName ?? "" })}</p>
            <p>
                {t("TransferTerms.DeleteModal.ContractLength")}
                {term && formatLocaleDateString({ date: new Date(term?.startDate) })}
                {" ↔︎ "}
                {term && formatLocaleDateString({ date: new Date(term?.expirationDate) })}
            </p>
        </>
    );

    return (
        <>
            <div className="transfer-form-container" style={{ display: addNewTransferee ? "none" : "" }}>
                <NewForm
                    steps={steps}
                    useFormData={{
                        trigger: trigger,
                        handleSubmit: handleSubmit,
                        isDirty: isDirty,
                        dirtyFields,
                    }}
                    onSubmit={onSubmit}
                    mode={mode}
                    onDelete={() => setShowDeleteModal(true)}
                    dataForSteps={dataForSteps}
                    txtBtnSubmitNew={t("TransferTerms.TermForm.CreateTerm")}
                    haveInDepthValuesChanged={hasTermStatusChanged || haveCountriesChanged}
                    deleteStep={deleteStep}
                    submitFormStatus={submitFormStatus}
                    infoLink={{
                        type: t("TransferTerms.TransferTerm").toLowerCase(),
                        link: "https://helpdesk.crealo.app/fr/article/creer-un-contrat-de-cession-1q21e6y/",
                    }}
                    handleScrollToErrors={() => {
                        handleScrollToErrors(errors);
                    }}
                />
                <DeleteModal
                    open={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onDelete={handleDeleteTerm}
                    elementTitle={t("TransferTerms.DeleteModal.ContractElement")}
                    description={deleteTermModalDescription}
                />
            </div>
            {addNewTransferee && (
                <TransfereesFormContainer
                    mode="new"
                    from="otherForm"
                    handleClose={handleClosingTransfereeForm}
                    setAddNewTransferee={setAddNewTransferee}
                />
            )}
        </>
    );
};

export default React.memo(TransferTermFormContainer);
